import { useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router';
import { AppFooter } from '../components/AppFooter';
import { Alert, Loading } from '../components/ui';
import { useI18n } from '../i18n';
import { useMe } from '../session';
import { ConsoleHeader } from './ConsolePage';
import { Ph, Say, SectionBar, useSalesPageBackground } from './sales-ui';

// The board demo script for sellers (Outils de vente → Script de la démo au conseil): 90 minutes, eight scenes with two
// live moments. This page is the reference text (docs/sales/demo-script.md points here): update it here, then deploy.

const SECTIONS: [string, string][] = [
  ['roles', 'Rôles'],
  ['preparer', 'Préparer'],
  ['deroulement', 'Déroulement'],
  ['scenes', 'Les scènes'],
  ['questions', 'Questions'],
  ['conclusion', 'Conclusion'],
  ['apres', 'Après'],
];

const PREPARATION: { key: string; when: string; what: ReactNode }[] = [
  { key: 'fiche', when: 'Après la découverte', what: <>Relire la fiche du prospect : leurs trois irritants, avec leurs mots. Ils structurent toute la démo.</> },
  { key: 'choix', when: 'La veille', what: <>Choisir la démo : <strong>Domaine des Hauts-Bois</strong> pour une association, <strong>Les Érables</strong> pour un syndicat.</> },
  {
    key: 'reglements',
    when: 'La veille',
    what: (
      <>
        Si le conseil a envoyé ses règlements <strong>et l’a accepté</strong> : les déposer dans Documents de la démo (visibilité « conseil »). Une réponse tirée de <em>leurs</em> règles vaut toute la
        présentation. La réinitialisation suivante les efface.
      </>
    ),
  },
  {
    key: 'reset',
    when: 'Le matin, au moins 1 h avant',
    what: (
      <>
        Outils de vente → la démo → <strong>Réinitialiser la démo</strong> (prête en une minute environ). Ensuite seulement, faire les préparatifs suivants : la réinitialisation les effacerait.
      </>
    ),
  },
  {
    key: 'fournisseur',
    when: 'Le matin',
    what: (
      <>
        Entretien → Fournisseurs : mettre l’adresse courriel du <strong>copilote</strong> sur le fournisseur de la barrière, pour que sa facture soit reconnue. Noter le numéro du bon « Barrière d’entrée bloquée
        ouverte » (Entretien → Bons de travail).
      </>
    ),
  },
  { key: 'telephone', when: 'Le matin', what: <>Préparer sur le téléphone du copilote : le courriel du propriétaire (scène 2) et une facture PDF qui mentionne « Bon no <Ph>[numéro]</Ph> » et un montant.</> },
  {
    key: 'essai',
    when: 'Le matin',
    what: (
      <>
        Faire un essai complet des deux courriels. Compter <strong>1 à 2 minutes</strong> entre l’envoi et l’affichage (les courriels sont relevés chaque minute, puis l’assistant prépare sa réponse).
      </>
    ),
  },
  {
    key: 'materiel',
    when: 'Sur place',
    what: (
      <>
        Ordinateur et écran ou télé, connexion (partage de connexion du téléphone en secours), la présentation aux associations en PDF si Internet tombe, le{' '}
        <Link to="/ventes/comparateur">comparateur de coûts</Link> ouvert dans un onglet.
      </>
    ),
  },
];

const RUN: [string, string, string][] = [
  ['0–5', 'Ouverture', 'Remercier, présenter l’ordre du jour, demander combien de temps ils ont vraiment.'],
  ['5–10', 'Leur situation', 'Reformuler ce qu’on a entendu à la découverte; leur demander de corriger.'],
  ['10–55', 'Un mois dans la vie de votre conseil', 'Huit scènes, dont deux en direct.'],
  ['55–85', 'Questions', 'Laisser le conseil parler; le copilote note.'],
  ['85–90', 'Conclusion', 'L’essai gratuit, une date, un champion.'],
];

const SCENES: { n: number; title: string; minutes: number; live?: boolean; body: ReactNode }[] = [
  {
    n: 1,
    title: 'L’accueil, le lundi matin',
    minutes: 3,
    body: (
      <>
        <p>Accueil : ce qui demande l’attention du conseil cette semaine. Approbations : ce que les assistants ont préparé et qui attend une décision.</p>
        <Say>« Le conseil ne cherche plus : ce qui demande une décision vient à lui, et rien ne part sans son accord. »</Say>
      </>
    ),
  },
  {
    n: 2,
    title: 'Un propriétaire écrit',
    minutes: 8,
    live: true,
    body: (
      <>
        <p>Au début de la scène, le copilote envoie depuis son téléphone, à <code>demandes@…</code>, un courriel comme :</p>
        <Say>« Bonjour, est-ce que je peux installer une clôture en bordure du chemin, devant mon terrain? Merci. »</Say>
        <p>
          Pendant le délai (1 à 2 minutes), montrer une demande déjà traitée dans Demandes : le brouillon de réponse et l’article des règlements cité. Puis revenir : la nouvelle demande est arrivée, avec son
          brouillon et sa source. Si leurs règlements ont été déposés : poser la question sur un sujet de <em>leurs</em> règles.
        </p>
        <Say>« Le propriétaire a écrit comme d’habitude, par courriel. Le conseil relit, corrige au besoin, envoie. »</Say>
      </>
    ),
  },
  {
    n: 3,
    title: 'Les cotisations',
    minutes: 7,
    body: (
      <>
        <p>
          Finances : les soldes par lot, les retards, les fonds séparés (fonctionnement, prévoyance). Approbations : le lot de rappels préparé, que le conseil approuve. La page affiche un avertissement : le
          relevé bancaire d’octobre n’est pas concilié. C’est voulu, et c’est un bon moment : on n’envoie pas de rappel à quelqu’un qui a peut-être déjà payé.
        </p>
        <Say>« Les rappels sont préparés à 15 et 30 jours; aucun ne part sans l’accord d’un administrateur. »</Say>
      </>
    ),
  },
  {
    n: 4,
    title: 'La banque',
    minutes: 4,
    body: (
      <>
        <p>Finances → Banque : un relevé importé, les dépôts associés automatiquement aux propriétaires, ce qui reste à confirmer.</p>
        <Say>« La conciliation bancaire du mois prend quelques minutes, et la comptable reçoit un dossier propre. »</Say>
      </>
    ),
  },
  {
    n: 5,
    title: 'La barrière est bloquée',
    minutes: 8,
    live: true,
    body: (
      <>
        <p>
          Entretien → le bon « Barrière d’entrée bloquée ouverte » : le fournisseur, le lien sécurisé qu’il reçoit (pas de compte à créer), les soumissions côte à côte. Au milieu de la scène, le copilote
          envoie la facture PDF à <code>factures@…</code>. Quelques minutes plus tard, la facture apparaît sur le bon de travail, vérifiée contre le montant prévu, prête à approuver; l’approbation l’inscrit aux
          finances.
        </p>
        <Say>« Votre déneigeur et vos entrepreneurs continuent d’envoyer leurs factures par courriel. Syndiqia fait le reste. »</Say>
        <p className="cg-callout">
          <strong>Si la facture tombe dans « Factures à classer »</strong> (adresse non reconnue) : le montrer tel quel, c’est aussi une fonctionnalité (« rien ne se perd, le conseil choisit le bon de travail
          suggéré »). <strong>Si elle n’arrive pas à temps</strong> : la démo contient déjà une facture en attente d’approbation et un travail avec des soumissions à comparer. Montrer celles-là, et revenir à la
          facture en direct pendant les questions.
        </p>
      </>
    ),
  },
  {
    n: 6,
    title: 'L’assemblée annuelle',
    minutes: 8,
    body: (
      <>
        <p>
          Assemblées : l’ordre du jour type selon leurs règlements, l’avis de convocation avec preuve de livraison, les présences et procurations, les votes, le procès-verbal rédigé à partir de
          l’enregistrement, le registre des résolutions.
        </p>
        <Say>« Le secrétaire ne passe plus une fin de semaine sur le procès-verbal : il le relit. »</Say>
      </>
    ),
  },
  {
    n: 7,
    title: 'Ce que voit un propriétaire',
    minutes: 3,
    body: (
      <>
        <p>Voir comme → Copropriétaire (ou Membre) : son lot, ses cotisations, ses demandes, les documents publics.</p>
        <Say>« Pour les membres, rien à installer : tout fonctionne aussi par courriel. »</Say>
      </>
    ),
  },
  {
    n: 8,
    title: 'La confiance',
    minutes: 4,
    body: (
      <>
        <p>
          Automatisation : chaque processus est en mode confirmation par défaut; on voit la précision mesurée avant de passer un processus en automatique. Puis en une phrase : données hébergées au Canada,
          journal infalsifiable que le conseil peut vérifier, exportation complète en tout temps, aucuns frais de sortie.
        </p>
        <Say>« Vous décidez, processus par processus, ce qui peut se faire seul. Et vous pouvez partir quand vous voulez, avec toutes vos données. »</Say>
      </>
    ),
  },
];

const QUESTIONS: [string, ReactNode][] = [
  ['Combien ça coûte pour nous?', <>Ouvrir le <Link to="/ventes/comparateur">comparateur de coûts</Link> avec leurs chiffres (nombre de lots, ce qu’ils paient aujourd’hui).</>],
  ['Nos données? La Loi 25?', 'Hébergées au Canada, accès par rôle, journal vérifiable, exportation en tout temps.'],
  ['Et si Syndiqia disparaît?', 'Exportation complète en formats standards, aucuns frais de sortie.'],
  ['L’IA peut se tromper.', 'Oui : c’est pour ça que tout commence en confirmation, et qu’on mesure la précision avant d’automatiser quoi que ce soit.'],
  ['Nos membres ne sont pas technos.', 'Tout fonctionne par courriel; l’application est facultative.'],
  ['Notre déneigeur devra utiliser votre plateforme?', 'Non : il envoie ses factures par courriel, comme aujourd’hui.'],
  ['On est sous contrat avec une firme.', 'Essai en parallèle pendant le préavis; on prépare la transition.'],
  ['Qui est responsable si quelque chose part mal?', 'Le conseil décide de tout ce qui est conséquent; avec Assistée et Complète, une personne de Syndiqia est responsable du suivi.'],
];

function Checklist() {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const n = PREPARATION.filter((p) => done[p.key]).length;
  return (
    <div className="cg-score">
      {PREPARATION.map((p) => (
        <label key={p.key}>
          <input type="checkbox" checked={!!done[p.key]} onChange={(e) => setDone({ ...done, [p.key]: e.target.checked })} />
          <span>
            <strong>{p.when}</strong>
            <span>{p.what}</span>
          </span>
        </label>
      ))}
      <div className="cg-result" aria-live="polite">
        <span className="cg-num">
          {n} / {PREPARATION.length}
        </span>
        <span className={`cg-pill ${n === PREPARATION.length ? 'cg-go' : 'cg-later'}`}>{n === PREPARATION.length ? 'Prêt pour la démo' : 'Préparation en cours'}</span>
      </div>
    </div>
  );
}

export function DemoScriptPage() {
  const { t } = useI18n();
  const me = useMe();
  useSalesPageBackground();
  if (me.isPending) return <Loading label={t.loading} />;
  if (me.isError) return <Alert>{t.common.error}</Alert>;
  if (!me.data.staff.includes('platform_admin') && !me.data.staff.includes('sales')) return <Navigate to="/" replace />;

  return (
    <div className="chooser console cg">
      <ConsoleHeader me={me.data} />
      <p>
        <Link to="/ventes">← {t.salesTools.title}</Link>
      </p>
      <p className="cg-eyebrow">Syndiqia · ventes</p>
      <h1>La démo au conseil</h1>
      <p className="cg-lede">
        Quatre-vingt-dix minutes devant tout le conseil, deux moments en direct. Le seul résultat à obtenir : un « oui » à l’essai gratuit de trois mois après la mise en service, avec une date. Le premier
        contact et la découverte sont dans le <Link to="/ventes/guide">guide d’appel</Link>.
      </p>

      <SectionBar sections={SECTIONS} />

      <section id="roles">
        <h2>Les rôles</h2>
        <div className="cg-rules">
          <div>
            <strong>Présentateur</strong>Mène la rencontre, montre l’écran, pose les questions.
          </div>
          <div>
            <strong>Copilote (fortement recommandé)</strong>Envoie les deux courriels en direct, surveille le temps, note les questions et les mots exacts des administrateurs. Seul, on peut le faire, mais les moments
            en direct sont plus risqués.
          </div>
        </div>
      </section>

      <section id="preparer">
        <h2>Préparer (la veille et le matin)</h2>
        <p>À cocher au fur et à mesure le jour de la démo.</p>
        <Checklist />
        <p className="muted">
          Adresses de la démo Hauts-Bois : <code>demandes@demo-hauts-bois.syndiqia.com</code> et <code>factures@demo-hauts-bois.syndiqia.com</code> (pour Les Érables : <code>demo-erables</code>).
        </p>
      </section>

      <section id="deroulement">
        <h2>Le déroulement</h2>
        <div className="cg-steps">
          {RUN.map(([time, title, text]) => (
            <div key={title}>
              <span className="cg-time">{time} min</span>
              <span>
                <strong>{title}.</strong> {text}
              </span>
            </div>
          ))}
        </div>
        <h3>Ouverture (0–5)</h3>
        <Say>
          « Merci de nous recevoir. Ce soir, on ne vous fait pas une présentation de logiciel : on vous montre un mois dans la vie d’un conseil comme le vôtre, avec un domaine de démonstration, et deux moments en
          direct. Ensuite, c’est à vous : toutes vos questions. On termine à <Ph>[heure]</Ph>. Ça vous va? »
        </Say>
        <h3>Leur situation (5–10)</h3>
        <Say>
          « À notre rencontre avec <Ph>[prénoms]</Ph>, on a retenu trois choses : <Ph>[irritant 1]</Ph>, <Ph>[irritant 2]</Ph>, <Ph>[irritant 3]</Ph>. Est-ce que c’est juste? Qu’est-ce qu’on oublie? »
        </Say>
        <p>
          Noter les corrections. Pendant la démo, revenir à leurs mots : « Vous parliez de <Ph>[irritant 1]</Ph>; voici ce que ça donne. »
        </p>
      </section>

      <section id="scenes">
        <h2>Un mois dans la vie de votre conseil (10–55)</h2>
        <p>
          Montrer chaque écran <strong>à partir d’un problème du conseil</strong>, jamais par menu.
        </p>
        <div className="ds-scenes">
          {SCENES.map((s) => (
            <article key={s.n} className={`ds-scene${s.live ? ' ds-live' : ''}`}>
              <header>
                <span className="ds-n">{s.n}</span>
                <h3>{s.title}</h3>
                <span className="ds-min">{s.minutes} min</span>
                {s.live && <span className="ds-badge">En direct</span>}
              </header>
              {s.body}
            </article>
          ))}
        </div>
      </section>

      <section id="questions">
        <h2>Questions (55–85)</h2>
        <p>Laisser venir. Réponses courtes, puis : « Est-ce que ça répond à votre question? » Les plus fréquentes :</p>
        <div className="cg-table">
          <table>
            <thead>
              <tr>
                <th>Question</th>
                <th>Réponse courte</th>
              </tr>
            </thead>
            <tbody>
              {QUESTIONS.map(([q, a]) => (
                <tr key={q}>
                  <td>« {q} »</td>
                  <td>{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>Une question sans réponse sûre : « Je vérifie et je vous réponds par écrit demain. » Et le faire.</p>
      </section>

      <section id="conclusion">
        <h2>Conclusion (85–90)</h2>
        <Say>
          « Pour résumer : vous nous avez parlé de <Ph>[irritant 1]</Ph>, <Ph>[irritant 2]</Ph>, <Ph>[irritant 3]</Ph>, et vous avez vu ce que Syndiqia en fait. La prochaine étape que je vous propose : un
          essai gratuit de trois mois. On met en service votre association avec votre trésorier ou votre secrétaire, en une ou deux séances. Pendant l’essai, tout est en mode confirmation : rien ne part sans
          vous. On fixe ensemble deux ou trois critères de succès, et à la fin, vous décidez. Les frais de mise en service ne sont facturés que si vous ne continuez pas. »
        </Say>
        <p>Si c’est l’une des premières associations :</p>
        <Say>
          « Et cette année, nous choisissons quelques associations fondatrices : trois mois gratuits et votre prix garanti trois ans, en échange d’une étude de cas et de vos commentaires chaque mois. Je pense que
          le <Ph>[Domaine X]</Ph> serait un excellent candidat. »
        </Say>
        <p>Puis, concrètement :</p>
        <ol>
          <li>« Qui serait notre personne-ressource pendant l’essai? » <span className="muted">(le champion, souvent le trésorier)</span></li>
          <li>« Quand pourrait-on faire la première séance de mise en service? » <span className="muted">(une date)</span></li>
          <li>« Qu’est-ce qui vous ferait dire, dans trois mois, que ça valait la peine? » <span className="muted">(les critères de succès : <Link to="/ventes/doc/trial-agreement">entente d’essai</Link>, annexe A)</span></li>
        </ol>
        <p>Si le conseil doit en discuter seul : « Bien sûr. Quand en parlez-vous? Je vous appelle le lendemain. » Noter la date.</p>
      </section>

      <section id="apres">
        <h2>Après la rencontre (le jour même)</h2>
        <ul>
          <li>Courriel au conseil : merci, les trois irritants, ce qu’ils ont vu, la proposition d’essai, les critères évoqués, la date de mise en service ou de rappel.</li>
          <li>Si les règlements ont été déposés dans la démo : les retirer, ou réinitialiser la démo.</li>
          <li>Mettre à jour la fiche du prospect : étape, prochaine action et date, questions restées ouvertes.</li>
          <li>Noter en une ligne ce qui a marché et ce qui a bloqué pendant la démo.</li>
        </ul>
      </section>
      <AppFooter />
    </div>
  );
}
